import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLatest } from '../hooks/useLatest';
import { pushModalLayer } from '../lib/modalLayers';
import { isPopupOpen } from '../lib/popupTrigger';

// кривая и длительности — те же, что у морфа useMorphDialog
const MORPH_EASE = 'cubic-bezier(0.32, 0.72, 0.28, 1)';
const OPEN_MS = 380;
const CLOSE_MS = 300;

// Настройку читаем в момент анимации: useReducedMotion Mantine стартует с undefined и в эффекте
// ставит false — на маунте каждого грида это был лишний полный рендер таблицы.
const reducedMotion = () =>
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const TABBABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface IOptions {
    rootRef: RefObject<HTMLDivElement | null>;
    /** Заглушка высоты на месте таблицы в потоке: в её геометрию режим схлопывается. */
    placeholderRef: RefObject<HTMLDivElement | null>;
    /** Кнопка входа — ей возвращается фокус после выхода. */
    triggerRef: RefObject<HTMLButtonElement | null>;
    /** До смены режима: таблица запоминает строку-якорь прокрутки. */
    onBeforeChange?: (next: boolean) => void;
    onChange?: (active: boolean) => void;
    initialActive?: boolean;
    /** Где у таблицы в потоке прилипли туллбар и шапка (stickyHeader), если она
     *  прокручена внутрь; null — не прилипли. Аргумент — узел таблицы в потоке
     *  (сама таблица или заглушка на её месте). */
    getStuckTop?: (flowNode: HTMLElement) => number | null;
    /**
     * Escape адресован режиму (стек окон: окна, меню и панели у кнопок над ним забирают его сами), но выйти сейчас
     * нельзя — `false`: Escape остаётся тому, что открыто внутри (редактор ячейки). Раскрытый попап внутри
     * (`isPopupOpen`) хук проверяет сам. Состояние снимается на перехвате, до обработчиков React.
     */
    canExitOnEscape?: () => boolean;
}

const FULL_INSET = 'inset(0px 0px 0px 0px round 0px)';
const NO_SHIFT = 'translate(0px, 0px)';

interface IBox {
    left: number;
    top: number;
    width: number;
    height: number;
}
/** Содержимое таблицы (зона данных) — опорная точка морфа: она не должна прыгать между видами. */
const contentOf = (root: HTMLElement) => root.querySelector<HTMLElement>(':scope > .dtb__content') ?? root;

/**
 * Кадр «таблица на своём месте в потоке» для слоя на весь экран. Слой сдвигается так, чтобы зона
 * данных совпала с её положением в потоке, а обрезка (в координатах до сдвига) открывает ровно
 * прямоугольник таблицы. Одной обрезки мало: содержимое слоя разложено от угла вьюпорта, и в окне
 * обрезки оказывался бы чужой фрагмент таблицы — на стыке видов картинка дёргалась.
 */
const flowFrame = (root: HTMLElement, box: IBox, contentOffset: { x: number; y: number }) => {
    const content = contentOf(root).getBoundingClientRect();
    const dx = box.left + contentOffset.x - content.left;
    const dy = box.top + contentOffset.y - content.top;
    const left = box.left - dx;
    const top = box.top - dy;
    const right = document.documentElement.clientWidth - (left + box.width);
    const bottom = document.documentElement.clientHeight - (top + box.height);
    return {
        clipPath: `inset(${top}px ${right}px ${bottom}px ${left}px round 14px)`,
        transform: `translate(${dx}px, ${dy}px)`,
    };
};
const FULL_FRAME = { clipPath: FULL_INSET, transform: NO_SHIFT };

/**
 * Прямоугольник посадки морфа. Таблица, прокрученная внутрь, видна не от своего верха: туллбар и
 * шапка прилипли к верху прокрутки, под ними строки. Садимся в видимую часть плитки от места
 * прилипания — шапка слоя ложится на прилипшую шапку, а не улетает за экран вместе с верхом
 * таблицы, чтобы в конце морфа выскочить на прежнем месте.
 */
export const landingBox = (box: IBox, stuckTop: number | null): IBox =>
    stuckTop == null || box.top >= stuckTop
        ? box
        : { left: box.left, top: stuckTop, width: box.width, height: box.top + box.height - stuckTop };

/** Предки, из-за которых `position: fixed` отсчитывается не от вьюпорта. */
const containingBlockAncestors = (el: HTMLElement): string[] => {
    const found: string[] = [];
    for (let node = el.parentElement; node && node !== document.documentElement; node = node.parentElement) {
        const cs = getComputedStyle(node);
        const reasons = [
            cs.transform !== 'none' && 'transform',
            cs.filter !== 'none' && 'filter',
            cs.perspective !== 'none' && 'perspective',
            cs.contain !== 'none' && `contain: ${cs.contain}`,
            cs.containerType && cs.containerType !== 'normal' && `container-type: ${cs.containerType}`,
            /transform|filter|perspective/.test(cs.willChange) && `will-change: ${cs.willChange}`,
        ].filter(Boolean);
        if (reasons.length)
            found.push(`${node.tagName.toLowerCase()}.${String(node.className).split(' ')[0]} (${reasons.join(', ')})`);
    }
    return found;
};

/**
 * Полноэкранный режим таблицы: тот же DOM-узел получает `position: fixed`, поэтому
 * состояние таблицы, открытый редактор и прокрутка по горизонтали переживают переход.
 * Вход и выход — морф clip-path между прямоугольником таблицы в потоке и вьюпортом.
 */
export const useDataTableFullscreen = ({
    rootRef,
    placeholderRef,
    triggerRef,
    onBeforeChange,
    onChange,
    initialActive = false,
    getStuckTop,
    canExitOnEscape,
}: IOptions) => {
    const [active, setActive] = useState(initialActive);
    const [placeholderHeight, setPlaceholderHeight] = useState(0);
    const fromRectRef = useRef<IBox | null>(null);
    // смещение зоны данных от угла таблицы в потоке — по нему выход находит место посадки
    const contentOffsetRef = useRef({ x: 0, y: 0 });
    const closingRef = useRef(false);
    const closeAnimationRef = useRef<Animation | null>(null);
    const closeTimerRef = useRef(0);
    const onBeforeChangeRef = useRef(onBeforeChange);
    onBeforeChangeRef.current = onBeforeChange;
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const getStuckTopRef = useRef(getStuckTop);
    getStuckTopRef.current = getStuckTop;
    const canExitOnEscapeRef = useLatest(canExitOnEscape);

    const canAnimate = useCallback(
        (el: HTMLElement | null): el is HTMLDivElement =>
            !!el && typeof el.animate === 'function' && !reducedMotion() && !document.hidden,
        [],
    );

    const enter = useCallback(() => {
        const root = rootRef.current;
        if (!root || active) return;
        const rect = root.getBoundingClientRect();
        const content = contentOf(root).getBoundingClientRect();
        fromRectRef.current = landingBox(rect, getStuckTopRef.current?.(root) ?? null);
        contentOffsetRef.current = { x: content.left - rect.left, y: content.top - rect.top };
        setPlaceholderHeight(rect.height);
        onBeforeChangeRef.current?.(true);
        setActive(true);
    }, [active, rootRef]);

    const exit = useCallback(() => {
        const root = rootRef.current;
        if (!root || !active || closingRef.current) return;
        const finish = () => {
            if (!closingRef.current) return;
            closingRef.current = false;
            onBeforeChangeRef.current?.(false);
            setActive(false);
        };
        closingRef.current = true;
        const target = placeholderRef.current?.getBoundingClientRect();
        // у таблицы, рождённой развёрнутой, заглушка пустая — схлопываться некуда, выходим сразу
        if (!canAnimate(root) || !target?.height) {
            finish();
            return;
        }
        const placeholder = placeholderRef.current!;
        const to = flowFrame(
            root,
            landingBox(target, getStuckTopRef.current?.(placeholder) ?? null),
            contentOffsetRef.current,
        );
        root.style.willChange = 'clip-path, transform';
        const animation = root.animate(
            { clipPath: [FULL_INSET, to.clipPath], transform: [NO_SHIFT, to.transform] },
            { duration: CLOSE_MS, easing: MORPH_EASE, fill: 'forwards' },
        );
        // обрезку снимает layout-эффект вместе с режимом — иначе кадр слоя без обрезки
        closeAnimationRef.current = animation;
        // В фоновой вкладке анимация не тикает — выход не ждёт её дольше положенного.
        const done = () => {
            window.clearTimeout(closeTimerRef.current);
            finish();
        };
        closeTimerRef.current = window.setTimeout(done, CLOSE_MS + 100);
        animation.finished.then(done, done);
    }, [active, canAnimate, placeholderRef, rootRef]);

    useLayoutEffect(() => {
        const root = rootRef.current;
        closeAnimationRef.current?.cancel();
        closeAnimationRef.current = null;
        if (root) root.style.willChange = '';
        if (!active || !root) return;
        const rect = root.getBoundingClientRect();
        const laidOut = rect.width > 0 || rect.height > 0;
        const fillsViewport =
            Math.abs(rect.left) < 1 &&
            Math.abs(rect.top) < 1 &&
            // clientWidth, а не innerWidth: тот включает классическую полосу прокрутки страницы
            Math.abs(rect.width - document.documentElement.clientWidth) < 2 &&
            Math.abs(rect.height - document.documentElement.clientHeight) < 2;
        if (laidOut && !fillsViewport) {
            console.error(
                'DataTable: полноэкранный режим недоступен — предок таблицы перехватывает position: fixed:',
                containingBlockAncestors(root),
            );
            setActive(false);
            return;
        }
        // Корень фокусируемым не делаем: mousedown по ячейке переносил бы фокус на него как на
        // ближайшего фокусируемого предка и выбивал из только что открытого редактора.
        root.querySelector<HTMLElement>('[data-autofocus]')?.focus({ preventScroll: true });
        // прямоугольник живёт до конца морфа: StrictMode прогоняет эффект дважды, и второй
        // проход обязан собрать ту же анимацию, которую отменил cleanup первого
        const from = fromRectRef.current;
        if (!canAnimate(root) || !from) {
            fromRectRef.current = null;
            return;
        }
        const start = flowFrame(root, from, contentOffsetRef.current);
        root.style.willChange = 'clip-path, transform';
        const animation = root.animate(
            { clipPath: [start.clipPath, FULL_FRAME.clipPath], transform: [start.transform, FULL_FRAME.transform] },
            { duration: OPEN_MS, easing: MORPH_EASE, fill: 'backwards' },
        );
        // Смена режима — тяжёлый кадр (скролл-контейнер, лента вместо страницы, перемер строк).
        // Морф стоит на первом кадре, пока он не отрисуется: иначе начало анимации съедается.
        animation.pause();
        let frame = requestAnimationFrame(() => {
            frame = requestAnimationFrame(() => animation.play());
        });
        animation.finished.then(
            () => {
                fromRectRef.current = null;
                root.style.willChange = '';
            },
            () => undefined,
        );
        return () => {
            cancelAnimationFrame(frame);
            animation.cancel();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    // Escape — слоем стека окон: окно, меню или панель, открытые над таблицей, закрываются первыми, а выход из режима не
    // закрывает окно, в котором стоит таблица
    const exitRef = useLatest(exit);
    useEffect(() => {
        if (!active) return;
        // Решение «Escape внутреннему» — на перехвате: к слушателю window на всплытии React (дискретное событие,
        // микрозадачи между слушателями) уже закрыл редактор или попап, обработавший Escape без stopPropagation.
        const heldInside = new WeakSet<Event>();
        const onKeyDownCapture = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            const root = rootRef.current;
            if ((root && isPopupOpen(root)) || canExitOnEscapeRef.current?.() === false) heldInside.add(event);
        };
        window.addEventListener('keydown', onKeyDownCapture, true);
        const popLayer = pushModalLayer({
            root: () => rootRef.current,
            dialog: () => rootRef.current,
            onEscape: (event) => {
                // узел вне слоя, но не его предок (портал пикера или панели строки) — Escape не режиму
                const root = rootRef.current;
                const target = event.target;
                if (root && target instanceof Node && !root.contains(target) && !target.contains(root)) return false;
                if (heldInside.has(event) || closingRef.current) return false;
                exitRef.current();
                return true;
            },
        });
        return () => {
            window.removeEventListener('keydown', onKeyDownCapture, true);
            popLayer();
        };
    }, [active, rootRef, canExitOnEscapeRef, exitRef]);

    // сверяем с прошлым значением, а не с фактом маунта: StrictMode прогоняет эффект дважды
    const reportedRef = useRef(active);
    useEffect(() => {
        if (reportedRef.current === active) return;
        reportedRef.current = active;
        onChangeRef.current?.(active);
        if (!active) triggerRef.current?.focus({ preventScroll: true });
    }, [active, triggerRef]);

    useEffect(
        () => () => {
            window.clearTimeout(closeTimerRef.current);
            closingRef.current = false;
        },
        [],
    );

    /** Tab не выходит за пределы слоя: форма под ним не должна получать фокус. */
    const trapTab = useCallback(
        (e: KeyboardEvent) => {
            const root = rootRef.current;
            if (!active || e.key !== 'Tab' || !root) return;
            const items = [...root.querySelectorAll<HTMLElement>(TABBABLE)].filter((el) => el.offsetParent !== null);
            if (!items.length) return;
            const first = items[0];
            const last = items[items.length - 1];
            const current = document.activeElement;
            // фокус на body — после клика по нефокусируемому месту слоя
            const outside = !root.contains(current);
            if (e.shiftKey && (current === first || outside)) {
                e.preventDefault();
                last.focus();
            } else if (!e.shiftKey && (current === last || outside)) {
                e.preventDefault();
                first.focus();
            }
        },
        [active, rootRef],
    );

    return { active, enter, exit, placeholderHeight, trapTab };
};
